import { use_slice_generation, use_slice_ui } from '@/hooks/store/use_slices';
import { UTILS__graph_data } from '@/utils/UTILS__graph_data';
import { assert_equal, assert_not_null } from '@shared/asserts/utils_asserts';
import { ENUM__string_id_empty } from '@shared/constants/ENUM__string_id_empty';
import { DTO__color } from '@shared/dto/+image/image_asset_base/DTO__color';
import { DTO__image_segment } from '@shared/dto/+image/image_asset_base/DTO__image_segment';
import { FIELD__image_asset } from '@shared/dto/generation/graph_invoke/_fields/image/FIELD__image_asset';
import { MouseEvent, useEffect, useRef, useState } from 'react';
import { DTO__image_segment_for_render } from './_data/DTO__image_segment_for_render';
import { DTO__image_segment_for_render_collection } from './_data/DTO__image_segment_for_render_collection';

interface PROPS {
  field: FIELD__image_asset;
}

export function G_IMAGE_OVERLAY__segments_interactive({ field }: PROPS) {
  /*
   * STORE
   */
  const { generation_current } = use_slice_generation(['generation_current']);
  const { g_general__ui_disabled, global__is_shift_pressed } = use_slice_ui(['g_general__ui_disabled', 'global__is_shift_pressed']);

  /*
   * STATE
   */
  const ref_canvas = useRef<HTMLCanvasElement | null>(null);

  const [hovered_segment_idx, set_hovered_segment_id] = useState<string>('');

  const ref_segments_data = useRef<DTO__image_segment_for_render_collection>(DTO__image_segment_for_render_collection.EMPTY);

  const { segments_map } = field.asset;

  /**
   * BUILD ALL MASKS / BITMAPS
   */
  useEffect(() => {
    async function build(): Promise<void> {
      if (segments_map.is_empty()) {
        ref_segments_data.current = DTO__image_segment_for_render_collection.EMPTY;

        render();

        return;
      }

      const first = segments_map.values()[0];

      const [width, height] = [first.mask_width, first.mask_height];
      const pixels_total = first.pixels_total;

      const built_segments: DTO__image_segment_for_render[] = [];

      for (const segment of segments_map.values()) {
        assert_equal(segment.pixels_total, pixels_total);

        const mask = new Uint8Array(pixels_total);
        segment.accumulate_to_mask(mask);

        /**
         * BORDER IMAGE
         */
        const border_image_data = new ImageData(width, height);

        /**
         * FULL FILL IMAGE
         */
        const fill_image_data = new ImageData(width, height);

        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            const idx = y * width + x;

            if (mask[idx] !== 1) continue;

            const offset = idx * 4;

            /**
             * FILL
             */
            DTO__color.COLOR_WHITE.apply_rgba(fill_image_data.data, offset);
            /**
             * BORDER DETECTION
             */
            const left = x > 0 ? mask[idx - 1] : 0;
            const right = x < width - 1 ? mask[idx + 1] : 0;
            const top = y > 0 ? mask[idx - width] : 0;
            const bottom = y < height - 1 ? mask[idx + width] : 0;

            const is_border = left === 0 || right === 0 || top === 0 || bottom === 0;

            if (!is_border) continue;

            DTO__color.COLOR_WHITE.apply_rgba(border_image_data.data, offset);
          }
        }

        const border_bitmap = await createImageBitmap(border_image_data);

        const fill_bitmap = await createImageBitmap(fill_image_data);

        built_segments.push({
          segment,
          mask,
          bitmap_border: border_bitmap,
          bitmap_fill: fill_bitmap,
        });
      }

      ref_segments_data.current = DTO__image_segment_for_render_collection.CREATE(built_segments, width, height);

      render();
    }

    void build();

    return () => void (ref_segments_data.current = DTO__image_segment_for_render_collection.EMPTY);
  }, [segments_map, field.hash_sha256_content]);

  /**
   * CREATE PATTERN
   */

  /**
   * RENDER
   */
  useEffect(() => render(), [hovered_segment_idx]);

  function render(): void {
    const canvas = ref_canvas.current;

    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    assert_not_null(ctx);

    const { segments, width, height } = ref_segments_data.current;

    if (width === 0 || height === 0) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    ctx.clearRect(0, 0, width, height);

    ctx.imageSmoothingEnabled = false;

    /**
     * DRAW BORDERS
     */
    for (let i = 0; i < segments.length; i++) {
      const data = segments[i];

      const is_hovered = hovered_segment_idx === data.segment.class_id;

      /**
       * 2px when hovered
       * 1px otherwise
       */
      const thickness = 0.15;
      let alpha = 1.0;

      if (hovered_segment_idx == '') {
        //do nothing
      } else if (is_hovered) {
        //do nothing
      } else {
        //canvas non hovered at all
        //or segment not hovered
        alpha = 0.25;
      }

      draw_white_border(
        {
          ctx,
          bitmap: data.bitmap_border,
          thickness,
        },
        alpha,
      );
    }

    /**
     * HOVER FILL
     */
    if (hovered_segment_idx !== null) {
      // const hovered = segments[hovered_segment_idx];
      // draw_hover_fill({ ctx, bitmap: hovered.bitmap_fill });
    }
  }

  function draw_white_border(params: { ctx: CanvasRenderingContext2D; bitmap: ImageBitmap; thickness: number }, alpha: number): void {
    const { ctx, bitmap, thickness } = params;

    ctx.save();

    // Set the overall opacity for this border group
    ctx.globalAlpha = alpha;

    /**
     * THICKEN BORDER
     */
    for (let ox = -thickness; ox <= thickness; ox++) {
      for (let oy = -thickness; oy <= thickness; oy++) {
        if (ox === 0 && oy === 0) continue;

        ctx.drawImage(bitmap, ox, oy);
      }
    }

    ctx.drawImage(bitmap, 0, 0);

    ctx.restore();
  }

  /**
   * HIT TEST
   */
  function get_segment_id_at_event(e: MouseEvent<HTMLCanvasElement>): string {
    if (g_general__ui_disabled) return '';

    const canvas = ref_canvas.current;
    if (!canvas) return '';

    const rect = canvas.getBoundingClientRect();

    const { segments, width, height } = ref_segments_data.current;

    if (width === 0 || height === 0) return '';

    const [scale_x, scale_y] = [width / rect.width, height / rect.height];

    const x = Math.floor((e.clientX - rect.left) * scale_x);
    const y = Math.floor((e.clientY - rect.top) * scale_y);

    if (x < 0 || x >= width || y < 0 || y >= height) return '';

    const pixel_index = y * width + x;

    /**
     * TOP -> BOTTOM
     */
    for (let i = segments.length - 1; i >= 0; i--) {
      let segment = segments[i];

      if (segment.mask[pixel_index] === 1) {
        return segment.segment.class_id;
      }
    }

    return '';
  }

  /*
   * EVENTS
   */

  function onMouseMove(e: MouseEvent<HTMLCanvasElement>): void {
    const class_id = get_segment_id_at_event(e);

    set_hovered_segment_id(class_id);
  }

  function onMouseLeave(): void {
    set_hovered_segment_id('');
  }

  async function onClick(e: MouseEvent<HTMLCanvasElement>) {
    const class_id = get_segment_id_at_event(e);

    if (class_id === '') return;

    const clicked_segment = ref_segments_data.current.segments.find_or_assert(item => item.segment.class_id == class_id);
    let clicked_class_id = clicked_segment.segment.class_id;

    let field_new = field.clone();

    let segments_disabled_ids_set = new Set(field_new.segments_disabled_ids);

    if (global__is_shift_pressed) {
      let segments_others = field_new.asset.segments_map.values().filter(segment => segment.class_id != clicked_class_id);

      let are_all_disabled = DTO__image_segment.are_all_disabled(segments_others, field.segments_disabled_ids);

      if (are_all_disabled) {
        //enable all others
        for (let segment_other of segments_others) segments_disabled_ids_set.delete(segment_other.class_id);
      } else {
        // disable all others
        for (let segment_other of segments_others) segments_disabled_ids_set.add(segment_other.class_id);
      }
    } else {
      if (segments_disabled_ids_set.has(clicked_class_id)) {
        segments_disabled_ids_set.delete(clicked_class_id);
      } else {
        segments_disabled_ids_set.add(clicked_class_id);
      }
    }

    field_new.segments_disabled_ids = Array.from(segments_disabled_ids_set);
    field_new.hash_sha256_content_segmented = ENUM__string_id_empty.HASH_SHA256;

    await UTILS__graph_data.update_field(generation_current.tool.graph, field_new);
  }

  /*
   * HTML
   */

  if (segments_map.is_empty()) return null;
  if (g_general__ui_disabled) return null;

  return (
    <canvas
      ref={ref_canvas}
      {...{ onMouseLeave, onClick, onMouseMove }}
      className={`
        absolute
        inset-0
        size-full
        image-render-pixelated
        pointer-events-auto
        ${hovered_segment_idx !== null ? 'cursor-pointer' : 'cursor-default'}
      `}
    />
  );
}
